import AnalyticsIcons from '@/icons/AnalyticsIcons'
import ContractIcons from '@/icons/ContractIcons'
import CustomerIcons from '@/icons/CustomerIcons'
import CustomerMktIcons from '@/icons/CustomerMktIcons'
import DashboardIcons from '@/icons/DashboardIcons'
import DriveIcons from '@/icons/DriveIcons'
import ExtensionIcons from '@/icons/ExtensionIcons'
import NewFolderIcons from '@/icons/FolderIcons/NewFolderIcons'
import InvoiceIcons from '@/icons/InvoiceIcons'
import PeopleIcons from '@/icons/PeopleIcons'
import PersonIcons from '@/icons/PersonIcons'
import RequirementsIcons from '@/icons/RequirementsIcons'
import ScheduleIcons from '@/icons/ScheduleIcons'
import ServiceIcons from '@/icons/ServiceIcons'
import SettingIcons from '@/icons/SettingIcons'
import TicketIcons from '@/icons/TicketIcons'
import WarehouseIcons from '@/icons/WarehouseIcons'
import { SidebarType } from '@/types/shared'
import { paths } from '@/utils/path'
import { resources, scopes } from '@/utils/permission'
import { Megaphone } from 'lucide-react'

/*
Sidebar title sử dụng namespace:key pattern của i18next.
Ví dụ: 'dashboard:sidebar_title' → lấy key "sidebar_title" từ namespace "dashboard"

Ba cách dùng permission trong sidebar:

1. ignorePermission: true - Bỏ qua kiểm tra permission (luôn hiển thị)
   Example: {
       icon: <DashboardIcons />,
       title: 'dashboard:sidebar_title',
       path: paths.home,
       ignorePermission: true,
   }

2. Single menu với permission - Menu đơn có kiểm tra quyền
   Example: {
       icon: <ServiceIcons />,
       title: 'services:sidebar_title',
       path: paths.services,
       rsname: resources.services,
       require_scopes: [scopes.index],
       visible_permission_denied: false,
   }

3. Parent menu với children - Menu cha có menu con, hiển thị khi ít nhất 1 child có quyền
   Example: {
       icon: <CustomerMktIcons />,
       title: 'marketing:sidebar_title',
       path: '',
       children: [
           {
               title: 'marketing:sidebar_contacts',
               path: paths.customerMkt,
               rsname: resources.contact,
               require_scopes: [scopes.index],
               visible_permission_denied: false,
           },
       ],
   }
*/

const docsUrl =
    import.meta.env?.APP_DOCUMENTATION_URL ||
    'https://production-dc-documentation.web.app/'

export const sidebarMenus: SidebarType[] = [
    {
        icon: <DashboardIcons />,
        title: 'dashboard:sidebar_title',
        path: paths.home,
        ignorePermission: true,
    },
    {
        icon: <ServiceIcons />,
        title: 'services:sidebar_title',
        path: paths.services,
        rsname: resources.services,
        require_scopes: [scopes.index],
        visible_permission_denied: false,
    },
    {
        icon: <InvoiceIcons />,
        title: 'invoice:sidebar_title',
        path: paths.invoices,
        rsname: resources.invoices,
        require_scopes: [scopes.index],
        visible_permission_denied: false,
    },
    {
        icon: <CustomerMktIcons />,
        title: 'marketing:sidebar_title',
        path: paths.customerMkt,
        rsname: resources.contact,
        require_scopes: [scopes.index],
        visible_permission_denied: false,
    },
    {
        icon: <CustomerIcons />,
        title: 'customer:sidebar_title',
        path: '',
        children: [
            {
                title: 'customer:sidebar_list',
                path: paths.customersList,
                rsname: resources.customers,
                require_scopes: [scopes.index],
                visible_permission_denied: false,
            },
            {
                title: 'reseller:sidebar_title',
                path: paths.resellersList,
                // `Resellers` is not a Keycloak resource → gate on Customers write instead, so the
                // widened API audience (CSKH, BOM, Sale) also sees the menu.
                rsname: resources.customers,
                require_scopes: [scopes.create],
                visible_permission_denied: false,
            },
        ],
    },
    {
        icon: <PeopleIcons />,
        title: 'employee:sidebar_hrm',
        path: '',
        children: [
            {
                title: 'employee:sidebar_employee_manage',
                path: paths.employeesManage,
                rsname: resources.employees,
                require_scopes: [scopes.index],
                visible_permission_denied: false,
            },
            {
                title: 'dayoffs:sidebar_title',
                path: paths.dayOffsManage,
                rsname: resources.dayoff,
                require_scopes: [scopes.index],
                visible_permission_denied: false,
            },
            {
                title: 'business:sidebar_title',
                path: paths.businessesManage,
                rsname: resources.business,
                require_scopes: [scopes.index],
                visible_permission_denied: false,
            },
            {
                title: 'workday:sidebar_title',
                path: paths.workdayManage,
                rsname: resources.workday,
                require_scopes: [scopes.index],
                visible_permission_denied: false,
            },
            {
                title: 'resume:sidebar_title',
                path: paths.talentPoolManager,
                rsname: resources.resume,
                require_scopes: [scopes.index],
                visible_permission_denied: false,
            },
        ],
    },
    {
        icon: <TicketIcons />,
        title: 'ticket:sidebar_title',
        path: paths.ticket,
        rsname: resources.tickets,
        require_scopes: [scopes.index],
        visible_permission_denied: false,
    },
    {
        icon: <ScheduleIcons />,
        title: 'oncall:oncall_management',
        path: '',
        ignorePermission: true,
        children: [
            {
                title: 'oncall:oncall_dashboard',
                path: paths.oncall,
            },
            {
                title: 'oncall:oncall_schedules',
                path: paths.oncallSchedules,
            },
            {
                title: 'oncall:oncall_assignments',
                path: paths.oncallAssignments,
            },
            {
                title: 'oncall:oncall_swaps',
                path: paths.oncallSwaps,
            },
            {
                title: 'oncall:oncall_compensations',
                path: paths.oncallCompensations,
            },
        ],
    },
    {
        icon: <WarehouseIcons />,
        title: 'capacities-resources',
        path: paths.warehouse,
        ignorePermission: true,
    },
    {
        icon: <ContractIcons />,
        title: 'contract:sidebar_title',
        path: '',
        children: [
            {
                title: 'contract:sidebar_customer',
                path: paths.customerContractList,
                rsname: resources.contractsCustomers,
                require_scopes: [scopes.index],
                visible_permission_denied: false,
            },
            {
                title: 'contract:sidebar_partner',
                path: paths.partnerContractList,
                // API widened to Contracts Partners OR Customers create/update (BOM, Finance,
                // CSKH, Sale). The sidebar HOC checks a single rsname; Contracts Customers `index`
                // is held by exactly {CSKH, BOM, Finance, Sale}, matching that audience without
                // dropping Finance (Resellers/Affiliates/Partners are not Keycloak resources).
                rsname: resources.contractsCustomers,
                require_scopes: [scopes.index],
                visible_permission_denied: false,
            },
            {
                title: 'contract:sidebar_sample',
                path: paths.sampleContractList,
                rsname: resources.contractsSample,
                require_scopes: [scopes.index],
                visible_permission_denied: false,
            },
            {
                title: 'contract:sidebar_negotiation',
                path: paths.contractNegotiation,
                rsname: resources.contractsCustomers,
                require_scopes: [scopes.index],
                visible_permission_denied: false,
            },
        ],
    },
    {
        icon: <Megaphone className="w-5 h-5" />,
        title: 'affiliate:sidebar_title',
        path: '',
        children: [
            {
                title: 'affiliate:sidebar_customer',
                path: paths.affiliatesList,
                // Show the menu to anyone granted the `Affiliates` resource (e.g. Marketing) or,
                // as a fallback, Customers write (CSKH, BOM, Sale) — matching the API gate.
                rsnames: [resources.affiliates, resources.customers],
                require_scopes: [scopes.create],
                visible_permission_denied: false,
            },
            {
                title: 'affiliate:sidebar_employee',
                path: paths.employeeAffiliatesList,
                rsname: resources.employees,
                require_scopes: [scopes.index],
                visible_permission_denied: false,
            },
        ],
    },
    {
        icon: <AnalyticsIcons />,
        title: 'report:sidebar_title',
        path: '',
        children: [
            {
                title: 'report:sidebar_customer',
                path: paths.reportCustomer,
                rsname: resources.reports,
                require_scopes: [scopes.index],
                visible_permission_denied: false,
            },
            {
                title: 'report:sidebar_service',
                path: paths.reportService,
                rsname: resources.reports,
                require_scopes: [scopes.index],
                visible_permission_denied: false,
            },
            {
                title: 'report:sidebar_income',
                path: paths.reportIncome,
                rsname: resources.reports,
                require_scopes: [scopes.index],
                visible_permission_denied: false,
            },
            {
                title: 'report:sidebar_revenues',
                path: paths.reportRevenue,
                rsname: resources.reports,
                require_scopes: [scopes.index],
                visible_permission_denied: false,
            },
            {
                title: 'report:sidebar_business_targets',
                path: paths.reportBusinessTargets,
                rsname: resources.reports,
                require_scopes: [scopes.index],
                visible_permission_denied: false,
            },
            {
                title: 'report:sidebar_targets_analytics',
                path: paths.reportTargetAnalytics,
                rsname: resources.reports,
                require_scopes: [scopes.index],
                visible_permission_denied: false,
            },
            {
                title: 'report:sidebar_financials',
                path: paths.reportFinance,
                rsname: resources.reports,
                require_scopes: [scopes.index],
                visible_permission_denied: false,
            },
            {
                title: 'report:sidebar_service_analytics',
                path: paths.reportAnalytics,
                rsname: resources.reports,
                require_scopes: [scopes.index],
                visible_permission_denied: false,
            },
            {
                title: 'report_waf:sidebar_title',
                path: paths.reportWAF,
                rsname: resources.reports,
                require_scopes: [scopes.index],
                visible_permission_denied: false,
            },
            {
                // Báo cáo gọi thẳng servicemgmt, gate bằng group Keycloak /Admin
                // (EmployeeRequired) chứ không qua resource nào của dc-be. Cố ý
                // KHÔNG khai báo rsname/require_scopes: withPermission khi đó chỉ
                // cho isAdmin thấy — đúng bằng tập người gọi được API.
                title: 'Báo cáo WAF FREE',
                path: paths.toolsWafFreeReport,
            },
        ],
    },
    {
        icon: <DriveIcons />,
        title: 'data-central-drives',
        path: paths.drive,
        rsname: resources.drives,
        require_scopes: [scopes.index],
        visible_permission_denied: false,
    },
    {
        icon: <NewFolderIcons />,
        title: 'asset:sidebar_title',
        path: paths.assetsList,
        ignorePermission: true,
    },
    {
        icon: <ExtensionIcons />,
        title: 'extension',
        path: '',
        children: [
            {
                title: 'tenders:sidebar_title',
                path: paths.tenders,
            },
            {
                title: 'surveys:sidebar_title',
                path: paths.surveys,
            },
            {
                title: 'mailers',
                path: paths.mailerCampaign,
            },
            {
                title: 'livechat',
                path: paths.livechat,
            },
            {
                title: 'Chuyển ảnh sang WebP',
                path: paths.toolsConvertWebp,
                ignorePermission: true,
            },
        ],
    },
    ...(docsUrl
        ? [
              {
                  icon: <RequirementsIcons />,
                  title: 'user_guide',
                  path: docsUrl,
                  ignorePermission: true,
                  external: true,
              } as SidebarType,
          ]
        : []),
    {
        icon: <SettingIcons />,
        title: 'settings:sidebar_title',
        path: '',
        ignorePermission: true,
        children: [
            {
                title: 'settings:sidebar_service_templates',
                path: paths.serviceTemplates,
                ignorePermission: true,
            },
            {
                title: 'settings:sidebar_sla_templates',
                path: paths.slaTemplates,
                ignorePermission: true,
            },
            {
                title: 'settings:sidebar_regulations',
                path: paths.regulations,
                rsname: resources.employees,
                require_scopes: [scopes.index],
                visible_permission_denied: false,
            },
            {
                title: 'settings:sidebar_product_service',
                path: paths.productServiceManagement,
                ignorePermission: true,
            },
            // {
            //     title: 'settings:sidebar_authorization',
            //     path: paths.authorization,
            //     ignorePermission: true,
            // },
            {
                title: 'settings:sidebar_data_access',
                path: paths.dataAccess,
                require_admin: true,
            },
        ],
    },
    {
        icon: <PersonIcons />,
        title: 'account',
        path: '',
        children: [
            {
                title: 'account',
                path: paths.account,
            },
            {
                title: 'business-account',
                path: paths.businessAccountList,
            },
            {
                title: 'day-off-account',
                path: paths.dayOffAccountList,
            },
            {
                title: 'vcard-list',
                path: paths.vcardList,
            },
            {
                title: 'workday-account-list',
                path: paths.workdayList,
            },
            {
                title: 'my-incomes',
                path: paths.accountMyIncomes,
            },
            {
                title: 'my-ratings',
                path: paths.accountMyRatings,
            },
            {
                // Khai rsname + require_scopes là thứ DUY NHẤT ẩn được tab với role khác:
                // Sidebar/Menu/Item/index.tsx bỏ qua kiểm quyền khi thiếu một trong hai.
                // Chọn 'create' chứ không 'index' — CSKH nằm ở nhánh write, lấy 'index'
                // sẽ ẩn mất đúng nhóm cần dùng.
                title: 'customer:calendar_events.sidebar_title',
                path: paths.accountCustomerEvents,
                rsname: 'Customers',
                require_scopes: ['create'],
                visible_permission_denied: false,
            },
        ],
        ignorePermission: true,
    },
]
